import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { successToastify, errorToastify } from '../utils/toastify.util.js';
import { apiMessage } from '../utils/apiError.util.js';
import { getApiErrorMessage } from "../utils/upload.util.js";
import {
  applyItemGroupDiscount,
  createItemGroupAPI,
  fetchItemGroupDetails,
  fetchItemGroupListAPI,
  updateItemGroupAPI,
  updateItemSpecialFlag,
  updateItemStatusPublish,
  updateItemPositions
} from '../apis/item.api.js';

const itemPackSavedAsDraft = (payload, metaStatus) => {
  const rs = payload?.result?.status;
  if (rs != null && rs !== undefined) return String(rs) === "0";
  return metaStatus === "draft";
};
const initialState = {
  isLoad: false,
  isBtnLoad: false,
  isRedirect: false,

  groupList: [],
  pagination: {
    total: 0,
    limit: 10,
    offset: 0,
    hasMore: false,
    page: 1,
    pages: 1,
  },
  lastQuery: {},

  formData: {},
  groupData: null,
  isPublish: false,
  isFetch: false,
  isUpdate: false,
  item_discount_id: "",
  isApplyDiscount: false
};

export const fetchGroupList = createAsyncThunk(
  "challenge/group-list",
  async (payload, thunkAPI) => {
    try {
      const response = await fetchItemGroupListAPI(payload);
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  }
);

export const createItemGroup = createAsyncThunk(
  "challenge/create-group",
  async (item, thunkAPI) => {
    try {
      const response = await createItemGroupAPI(item);
      const state = thunkAPI.getState();
      const lastQuery = state?.item?.lastQuery || {};
      thunkAPI.dispatch(fetchGroupList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  }
);

export const setItemPublish = createAsyncThunk(
  "challenge/setPublishGroup",
  async (item, thunkAPI) => {
    try {
      const response = await updateItemStatusPublish(item);
      const state = thunkAPI.getState();
      const lastQuery = state?.item?.lastQuery || {};
      thunkAPI.dispatch(fetchGroupList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  }
);

export const setItemSpecialFlag = createAsyncThunk(
  "challenge/updateSpecialFlag",
  async (item, thunkAPI) => {
    try {
      const response = await updateItemSpecialFlag(item);
      const state = thunkAPI.getState();
      const lastQuery = state?.item?.lastQuery || {};
      thunkAPI.dispatch(fetchGroupList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  }
);

export const fetchGroupDetails = createAsyncThunk(
  "challenge/fetchGroupDetails",
  async (payload, thunkAPI) => {
    try {
      const response = await fetchItemGroupDetails(payload);
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  }
);

export const updateItemGroup = createAsyncThunk(
  "challenge/update-group",
  async (item, thunkAPI) => {
    try {
      const response = await updateItemGroupAPI(item);
      const state = thunkAPI.getState();
      const lastQuery = state?.item?.lastQuery || {};
      thunkAPI.dispatch(fetchGroupList(lastQuery));
      return response;
    } catch (err) {
      const data = err?.response?.data || {
        success: false,
        message: getApiErrorMessage(err),
      };
      return thunkAPI.rejectWithValue(data);
    }
  }
);

export const applyItemDiscount = createAsyncThunk(
  "challenge/applyItemDiscount",
  async (item, thunkAPI) => {
    try {
      const response = await applyItemGroupDiscount(item);
      if (response?.success) {
        const msg = response?.mesasge || "Discount applied successfully";
        successToastify(msg);
        thunkAPI.dispatch(setApplyDiscountSuccess());
        const state = thunkAPI.getState();
        const lastQuery = state?.item?.lastQuery || {};
        thunkAPI.dispatch(fetchGroupList(lastQuery));
      }
      return response;
    } catch (err) {
      const data = err?.response?.data;
      if (data?.message) errorToastify(data.message);
      return thunkAPI.rejectWithValue(
        data || {
          success: false,
          message: apiMessage(err?.response?.data, getApiErrorMessage(err)),
        },
      );
    }
  }
);

export const updateItemOrder = createAsyncThunk(
  "challenge/updateItemOrder",
  async ({ groupId, itemOrder }, thunkAPI) => {
    try {
      const response = await updateItemPositions({ groupId, itemOrder });
      if (response?.success) {
        successToastify("Challenge order updated");
      } else {
        errorToastify(response?.message || "Failed to update challenge order");
      }
      return response;
    } catch (err) {
      errorToastify("Failed to update challenge order");
      return thunkAPI.rejectWithValue(err?.response?.data || { success: false });
    }
  }
);

const itemSlice = createSlice({
  name: "item",
  initialState,
  reducers: {
    setFormData: (state, { payload }) => {
      state.formData = { ...state.formData, ...payload };
    },
    setItemDiscountId: (state, { payload }) => {
      state.item_discount_id = payload;
    },
    setApplyDiscountSuccess: (state) => {
      state.isApplyDiscount = true;
      state.item_discount_id = "";
    },
    setRedirect: (state, { payload }) => {
      state.isRedirect = payload;
    },
  },
  extraReducers: (builder) => {
    // ====== GROUP LIST ======
    builder.addCase(fetchGroupList.pending, (state, action) => {
      state.isLoad = true;
      state.isUpdate = false;
      state.groupData = null;

      // Remember the exact query used (keeps filters/limit/offset stable on refetch)
      state.lastQuery = action.meta?.arg || {};
      if (state.lastQuery?.limit !== undefined) state.pagination.limit = Number(state.lastQuery.limit);
      if (state.lastQuery?.offset !== undefined) state.pagination.offset = Number(state.lastQuery.offset);
    });

    builder.addCase(fetchGroupList.fulfilled, (state, action) => {
      state.isLoad = false;
      state.isUpdate = false;
      state.groupData = null;
      state.isApplyDiscount = false;

      const payload = action.payload || {};

      if (payload.success === false || payload.status === 'error') {
        errorToastify(payload.message || payload.mesasge || "Failed to fetch");
        return;
      }

      // ---- NORMALIZE RESPONSE SHAPES ----
      // Preferred (your API): { result: { data: [...], pagination: {...} } }
      const resultContainer = (payload.result && !Array.isArray(payload.result)) ? payload.result : null;

      const data =
        (resultContainer && Array.isArray(resultContainer.data)) ? resultContainer.data :
        (Array.isArray(payload.data)) ? payload.data :
        (Array.isArray(payload.result)) ? payload.result : [];

      state.groupList = data;

      // Pagination source: nested result.pagination -> top-level pagination -> fallback
      const pgSrc = (resultContainer && resultContainer.pagination)
        ? resultContainer.pagination
        : (payload.pagination || {});

      const total  = Number(pgSrc.total ?? 0);
      const limit  = Number(pgSrc.limit ?? state.pagination.limit ?? 10);
      const offset = Number(pgSrc.offset ?? state.pagination.offset ?? 0);
      const hasMore = typeof pgSrc.hasMore === "boolean" ? pgSrc.hasMore : (offset + limit < total);
      const page  = Math.floor(offset / limit) + 1;
      const pages = Math.max(1, Math.ceil((total || 1) / limit));

      state.pagination = { total, limit, offset, hasMore, page, pages };
    });

    builder.addCase(fetchGroupList.rejected, (state, action) => {
      state.isLoad = false;
      state.isUpdate = false;
      state.groupData = null;
      const message = action.payload?.message || "Request is rejected.";
      errorToastify(message);
    });

    // ====== CREATE GROUP ======
    builder.addCase(createItemGroup.pending, (state) => {
      state.isBtnLoad = true;
    });
    builder.addCase(createItemGroup.fulfilled, (state, action) => {
      state.isBtnLoad = false;
      const payload = action.payload || {};
      const ok =
        payload.success === true ||
        payload.code === 200 ||
        payload.status === "success";
      if (ok) {
        state.isRedirect = true;
        const savedAsDraft = itemPackSavedAsDraft(payload, action.meta?.arg?.status);
        successToastify(
          savedAsDraft
            ? "Challenge pack saved as draft"
            : "Challenge pack published to store",
        );
      } else {
        errorToastify(
          payload.message || payload.mesasge || "Failed to create package",
        );
      }
    });
    builder.addCase(createItemGroup.rejected, (state, action) => {
      state.isBtnLoad = false;
      const payload = action.payload || {};
      errorToastify(
        payload.message ||
          payload.mesasge ||
          getApiErrorMessage({ response: { data: payload } }) ||
          "Request is rejected.",
      );
    });

    // ====== PUBLISH / UNPUBLISH ======
    builder.addCase(setItemPublish.pending, (state) => {
      state.isPublish = true;
    });
    builder.addCase(setItemPublish.fulfilled, (state, action) => {
      state.isPublish = false;
      const { message, success } = action.payload || {};
      if (success) {
        successToastify("Status Updated Successfully");
      } else {
        errorToastify(message || "Failed to update status");
      }
    });
    builder.addCase(setItemPublish.rejected, (state, action) => {
      state.isPublish = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    builder.addCase(setItemSpecialFlag.pending, (state) => {
      state.isPublish = true;
    });
    builder.addCase(setItemSpecialFlag.fulfilled, (state, action) => {
      state.isPublish = false;
      const { message, success } = action.payload || {};
      if (success) {
        successToastify("Special flag updated successfully");
      } else {
        errorToastify(message || "Failed to update special flag");
      }
    });
    builder.addCase(setItemSpecialFlag.rejected, (state, action) => {
      state.isPublish = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    // ====== GROUP DETAILS ======
    builder.addCase(fetchGroupDetails.pending, (state) => {
      state.isFetch = true;
      state.isUpdate = false;
    });
    builder.addCase(fetchGroupDetails.fulfilled, (state, action) => {
      state.isFetch = false;
      state.isUpdate = false;
      if (action.payload?.success) {
        state.groupData = action.payload.result;
      } else {
        errorToastify(action.payload?.message || "Failed to fetch details");
      }
    });
    builder.addCase(fetchGroupDetails.rejected, (state, action) => {
      state.isFetch = false;
      state.isUpdate = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    // ====== UPDATE GROUP ======
    builder.addCase(updateItemGroup.pending, (state) => {
      state.isBtnLoad = true;
      state.isUpdate = false;
    });
    builder.addCase(updateItemGroup.fulfilled, (state, action) => {
      state.isBtnLoad = false;
      const { message, success } = action.payload || {};
      if (success) {
        state.isUpdate = true;
        state.isRedirect = true;
        const savedAsDraft = itemPackSavedAsDraft(
          action.payload,
          action.meta?.arg?.status,
        );
        successToastify(
          savedAsDraft
            ? "Challenge pack saved as draft"
            : "Challenge pack published to store",
        );
      } else {
        state.isUpdate = false;
        errorToastify(message || "Failed to update package");
      }
    });
    builder.addCase(updateItemGroup.rejected, (state, action) => {
      state.isBtnLoad = false;
      state.isUpdate = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    // ====== APPLY DISCOUNT ======
    builder.addCase(applyItemDiscount.pending, (state) => {
      state.isBtnLoad = true;
      state.isUpdate = false;
    });
    builder.addCase(applyItemDiscount.fulfilled, (state) => {
      state.isBtnLoad = false;
      // Success toast and refetch are already handled in the thunk
    });
    builder.addCase(applyItemDiscount.rejected, (state, action) => {
      state.isBtnLoad = false;
      state.isApplyDiscount = false;
      errorToastify(action.payload?.message || "Request is rejected.");
    });

    // ====== UPDATE ITEM ORDER ======
    builder.addCase(updateItemOrder.pending, (state) => {});
    builder.addCase(updateItemOrder.fulfilled, (state, action) => {});
    builder.addCase(updateItemOrder.rejected, (state, action) => {});
  }
});

export default itemSlice.reducer;
export const {
  setFormData,
  setItemDiscountId,
  setApplyDiscountSuccess,
  setRedirect
} = itemSlice.actions;
